import { isEditorImportReloadCorrelationTransaction } from "../../components/Unityboards/UnityBridge";
import type { UnitySpaceNavigationStatePayload } from "../../types/unity";
import {
  confirmSpaceNavigation,
  completeSpaceNavigationFromUnity,
  markSpaceNavigationCanonicalStateSeen,
  markSpaceNavigationRuntimeRestored,
  markSpaceNavigationSelecting,
  markSpaceNavigationVseModelReady,
} from "./spaceNavigationCoordinator";
import {
  getActiveSpaceSwitchTargetIndex,
  getActiveSpaceSwitchStage,
  getActiveSpaceSwitchTransactionId,
  getActiveSpaceSwitchTransactionType,
  isSpaceSwitchActive,
  isLatestSpaceSwitchEvent,
  isTerminalSpaceSwitchStage,
  setSpaceSwitchTargetIndex,
} from "./spaceSwitchState";
import { logEditorCorrelation } from "../../utils/editor";
import { useViewRDataStore } from "../../store/viewRDataStore";

export const UNITY_SPACE_NAVIGATION_STATE_EVENT = "unity:spaceNavigationState";
export const CANONICAL_UPLOAD_TARGET_CAPTURE_SOURCE =
  "unity_canonical_space_navigation" as const;
export const SPACE_NAVIGATION_COMPLETED_UPLOAD_TARGET_CAPTURE_SOURCE =
  "unity_space_navigation_completed" as const;
export const REACT_PENDING_SPACE_ADD_UPLOAD_TARGET_CAPTURE_SOURCE =
  "react_pending_space_add" as const;
export const REACT_VIEWR_DATA_UPLOAD_TARGET_CAPTURE_SOURCE =
  "react_viewr_data_store" as const;

const __DEV_CANONICAL_DEBUG__ =
  typeof window !== "undefined" &&
  (window.location?.search?.includes("debugCanonical=1") ||
    window.location?.hostname === "localhost" ||
    window.location?.hostname?.endsWith(".web.app") ||
    window.location?.hostname?.endsWith(".firebaseapp.com"));

/** Unity canonical index first, then React store — used to detect sidebar/view desync. */
export function getCanonicalEditorSpaceIndex(): number {
  if (typeof window !== "undefined") {
    const unityIndex = window.__viewr_currentSpaceIndex;
    if (
      typeof unityIndex === "number" &&
      Number.isFinite(unityIndex) &&
      unityIndex >= 0
    ) {
      return Math.trunc(unityIndex);
    }
  }

  const storeIndex = useViewRDataStore.getState().currentSpaceIndex;
  if (
    typeof storeIndex === "number" &&
    Number.isFinite(storeIndex) &&
    storeIndex >= 0
  ) {
    return Math.trunc(storeIndex);
  }

  return 0;
}

export function isEditorSpaceDisplaySynced(
  uiIndex: number,
  ledgerSpaceIndex: number,
): boolean {
  const canonicalIndex = getCanonicalEditorSpaceIndex();
  return (
    uiIndex === ledgerSpaceIndex && uiIndex === canonicalIndex
  );
}

type CanonicalTrackerState = {
  transactionId: string | null;
  highestPhaseRank: number;
  viewrDataRevision: number | null;
  projectIdentity: string | null;
  canonicalSeen: boolean;
  operation: string | null;
};

type CanonicalPhaseTimingState = {
  transactionId: string | null;
  startedAtMs: number | null;
  lastPhase: UnitySpaceNavigationStatePayload["phase"] | null;
  lastAtMs: number | null;
};

export type CanonicalSpaceSnapshotForUpload = {
  currentSpaceIndex: number | null;
  targetSpaceIndex: number | null;
  projectIdentity?: string | null;
  phase: UnitySpaceNavigationStatePayload["phase"] | null;
  status: UnitySpaceNavigationStatePayload["status"] | null;
  transactionId: string | null;
  spacesCount: number | null;
  isStable: boolean;
  captureSource:
    | typeof CANONICAL_UPLOAD_TARGET_CAPTURE_SOURCE
    | typeof SPACE_NAVIGATION_COMPLETED_UPLOAD_TARGET_CAPTURE_SOURCE
    | typeof REACT_PENDING_SPACE_ADD_UPLOAD_TARGET_CAPTURE_SOURCE
    | typeof REACT_VIEWR_DATA_UPLOAD_TARGET_CAPTURE_SOURCE;
};

export type UploadSpaceTargetForImport = CanonicalSpaceSnapshotForUpload & {
  readiness: "ready" | "pending_space_add" | "unavailable";
  pendingConfirmation: boolean;
  reason: string | null;
};

export type WaitForUploadSpaceTargetOptions = {
  timeoutMs?: number;
  signal?: AbortSignal;
};

const trackerState: CanonicalTrackerState = {
  transactionId: null,
  highestPhaseRank: -1,
  viewrDataRevision: null,
  projectIdentity: null,
  canonicalSeen: false,
  operation: null,
};

const timingState: CanonicalPhaseTimingState = {
  transactionId: null,
  startedAtMs: null,
  lastPhase: null,
  lastAtMs: null,
};

let latestAcceptedPayload: UnitySpaceNavigationStatePayload | null = null;

const PHASE_RANK: Record<UnitySpaceNavigationStatePayload["phase"], number> = {
  rehydrate_received: 0,
  started: 0,
  viewr_data_ready: 1,
  runtime_restored: 1,
  restore_ready: 2,
  awaiting_space_confirmation: 3,
  target_space_applied: 4,
  space_confirmed: 4,
  vse_model_ready: 5,
  stabilized: 6,
  confirmed: 7,
  completed: 7,
  failed: 8,
};

function getPhaseRank(
  phase: UnitySpaceNavigationStatePayload["phase"],
): number {
  return PHASE_RANK[phase];
}

function isStableUploadPhase(
  phase: UnitySpaceNavigationStatePayload["phase"] | null | undefined,
): boolean {
  return (
    phase === "space_confirmed" ||
    phase === "vse_model_ready" ||
    phase === "stabilized" ||
    phase === "confirmed" ||
    phase === "completed"
  );
}

function isSpaceSwitchInProgressForUpload(): boolean {
  if (!isSpaceSwitchActive()) {
    return false;
  }
  const stage = getActiveSpaceSwitchStage();
  return !isTerminalSpaceSwitchStage(stage);
}

export function getCanonicalSpaceSnapshotForUpload(): CanonicalSpaceSnapshotForUpload {
  const payload = latestAcceptedPayload;
  const activeTransactionId =
    typeof window !== "undefined"
      ? window.__viewr_spaceSwitchTransactionId ?? null
      : null;
  const activeTargetSpaceIndex =
    typeof window !== "undefined" &&
    typeof window.__viewr_spaceSwitchTargetIndex === "number"
      ? window.__viewr_spaceSwitchTargetIndex
      : null;
  const matchesActiveTransaction =
    !activeTransactionId || payload?.transactionId === activeTransactionId;
  const payloadCurrentSpaceIndex = normalizeSpaceIndex(payload?.currentSpaceIndex);
  const payloadTargetSpaceIndex = normalizeSpaceIndex(payload?.targetSpaceIndex);
  const matchesActiveTarget =
    activeTargetSpaceIndex === null ||
    payloadTargetSpaceIndex === activeTargetSpaceIndex ||
    payloadCurrentSpaceIndex === activeTargetSpaceIndex;
  const currentSpaceIndex = payloadCurrentSpaceIndex;
  const phase = payload?.phase ?? null;
  const isStable =
    currentSpaceIndex !== null &&
    isStableUploadPhase(phase) &&
    matchesActiveTransaction &&
    matchesActiveTarget &&
    !isSpaceSwitchInProgressForUpload();

  return {
    currentSpaceIndex,
    targetSpaceIndex: payloadTargetSpaceIndex,
    projectIdentity: payload?.projectIdentity ?? null,
    phase,
    status: payload?.status ?? null,
    transactionId: payload?.transactionId ?? null,
    spacesCount:
      typeof payload?.spacesCount === "number" ? payload.spacesCount : null,
    isStable,
    captureSource: CANONICAL_UPLOAD_TARGET_CAPTURE_SOURCE,
  };
}

function readPendingAddSpaceUploadTarget(): UploadSpaceTargetForImport | null {
  if (typeof window === "undefined") {
    return null;
  }

  const transactionType = getActiveSpaceSwitchTransactionType();
  if (transactionType !== "space_add") {
    return null;
  }

  const pendingTransactionId = window.__viewr_pendingAddSpaceTransactionId ?? null;
  const activeTransactionId = getActiveSpaceSwitchTransactionId();
  const transactionId = pendingTransactionId ?? activeTransactionId;
  const activeTargetSpaceIndex = getActiveSpaceSwitchTargetIndex();
  const pendingSpaceIndex =
    typeof window.__viewr_pendingAddSpaceIndex === "number" &&
    Number.isFinite(window.__viewr_pendingAddSpaceIndex)
      ? Math.trunc(window.__viewr_pendingAddSpaceIndex)
      : typeof activeTargetSpaceIndex === "number"
        ? activeTargetSpaceIndex
        : null;

  if (!transactionId || pendingSpaceIndex === null || pendingSpaceIndex < 0) {
    return null;
  }

  if (
    activeTransactionId &&
    pendingTransactionId &&
    activeTransactionId !== pendingTransactionId
  ) {
    return null;
  }

  return {
    currentSpaceIndex: null,
    targetSpaceIndex: pendingSpaceIndex,
    phase: "awaiting_space_confirmation",
    status: "in_progress",
    transactionId,
    spacesCount: null,
    isStable: false,
    captureSource: REACT_PENDING_SPACE_ADD_UPLOAD_TARGET_CAPTURE_SOURCE,
    readiness: "pending_space_add",
    pendingConfirmation: true,
    reason: "awaiting_unity_space_add_confirmation",
  };
}

function readReactViewRDataUploadTarget(): UploadSpaceTargetForImport | null {
  if (typeof window === "undefined" || isSpaceSwitchInProgressForUpload()) {
    return null;
  }

  const { viewRData, currentSpaceIndex, spacesCount } =
    useViewRDataStore.getState();
  const spaces = Array.isArray(viewRData?.spaces) ? viewRData.spaces : [];
  const effectiveSpacesCount =
    spaces.length > 0
      ? spaces.length
      : typeof spacesCount === "number" && Number.isFinite(spacesCount)
        ? Math.max(0, Math.trunc(spacesCount))
        : 0;

  if (effectiveSpacesCount <= 0) {
    return null;
  }

  const candidate =
    typeof currentSpaceIndex === "number" && Number.isFinite(currentSpaceIndex)
      ? currentSpaceIndex
      : typeof viewRData?.currentSelectVSIndex === "number" &&
          Number.isFinite(viewRData.currentSelectVSIndex)
        ? viewRData.currentSelectVSIndex
        : typeof window.__viewr_currentSpaceIndex === "number" &&
            Number.isFinite(window.__viewr_currentSpaceIndex)
          ? window.__viewr_currentSpaceIndex
          : 0;
  const resolvedIndex = Math.min(
    Math.max(Math.trunc(candidate), 0),
    effectiveSpacesCount - 1,
  );

  return {
    currentSpaceIndex: resolvedIndex,
    targetSpaceIndex: resolvedIndex,
    phase: "completed",
    status: "success",
    transactionId: null,
    spacesCount: effectiveSpacesCount,
    isStable: true,
    captureSource: REACT_VIEWR_DATA_UPLOAD_TARGET_CAPTURE_SOURCE,
    readiness: "ready",
    pendingConfirmation: false,
    reason: null,
  };
}

export function getUploadSpaceTargetForImport(): UploadSpaceTargetForImport {
  const canonicalSnapshot = getCanonicalSpaceSnapshotForUpload();
  if (
    canonicalSnapshot.isStable &&
    typeof canonicalSnapshot.currentSpaceIndex === "number" &&
    Number.isFinite(canonicalSnapshot.currentSpaceIndex) &&
    canonicalSnapshot.currentSpaceIndex >= 0
  ) {
    return {
      ...canonicalSnapshot,
      readiness: "ready",
      pendingConfirmation: false,
      reason: null,
    };
  }

  const pendingAddSpaceTarget = readPendingAddSpaceUploadTarget();
  if (pendingAddSpaceTarget) {
    return pendingAddSpaceTarget;
  }

  const reactViewRDataTarget = readReactViewRDataUploadTarget();
  if (reactViewRDataTarget) {
    return reactViewRDataTarget;
  }

  return {
    ...canonicalSnapshot,
    readiness: "unavailable",
    pendingConfirmation: false,
    reason: canonicalSnapshot.isStable
      ? "invalid_canonical_current_space_index"
      : "canonical_space_not_stable",
  };
}

export function resolveReadyUploadSpaceIndex(
  target: UploadSpaceTargetForImport = getUploadSpaceTargetForImport(),
): number | null {
  if (
    target.readiness !== "ready" ||
    typeof target.currentSpaceIndex !== "number" ||
    !Number.isFinite(target.currentSpaceIndex) ||
    target.currentSpaceIndex < 0
  ) {
    return null;
  }

  return Math.trunc(target.currentSpaceIndex);
}

function isResolvedUploadSpaceTarget(
  target: UploadSpaceTargetForImport,
): boolean {
  return target.readiness === "ready" || target.readiness === "pending_space_add";
}

export type ImportEntryUploadSpaceTarget = {
  spaceIndex: number;
  transactionId: string | null;
  pendingConfirmation: boolean;
};

/**
 * Import 진입 UI(Import to space 버튼)용 target 해석.
 *
 * `resolveReadyUploadSpaceIndex`와 달리 Add Space 확정 전(`pending_space_add`)도
 * 허용한다. STUDIO-SPACE-003에 따라 Import 진입은 canonical 확정과 persist 완료를
 * 기다리지 않는다. 확정 전 target은 attach 제출 직전 preflight가 Unity canonical과
 * 대조해 확정하거나 실패시키므로, 여기서 통과시켜도 다른 Space에 적재되지 않는다.
 *
 * 확정 전 target은 그 Space를 만든 transactionId를 함께 돌려준다. preflight가 이
 * transactionId 기준으로 확정/실패를 판정한다.
 */
export function resolveImportEntryUploadSpaceTarget(
  target: UploadSpaceTargetForImport = getUploadSpaceTargetForImport(),
): ImportEntryUploadSpaceTarget | null {
  const candidate =
    target.readiness === "ready"
      ? target.currentSpaceIndex
      : target.readiness === "pending_space_add"
        ? target.targetSpaceIndex
        : null;

  if (
    candidate === null ||
    typeof candidate !== "number" ||
    !Number.isFinite(candidate) ||
    candidate < 0
  ) {
    return null;
  }

  return {
    spaceIndex: Math.trunc(candidate),
    transactionId: target.transactionId ?? null,
    pendingConfirmation: target.readiness === "pending_space_add",
  };
}

export function waitForUploadSpaceTargetForImport({
  timeoutMs = 1200,
  signal,
}: WaitForUploadSpaceTargetOptions = {}): Promise<UploadSpaceTargetForImport> {
  const currentTarget = getUploadSpaceTargetForImport();
  if (
    isResolvedUploadSpaceTarget(currentTarget) ||
    typeof window === "undefined" ||
    signal?.aborted ||
    timeoutMs <= 0
  ) {
    return Promise.resolve(currentTarget);
  }

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: number | null = null;

    const cleanup = () => {
      window.removeEventListener(
        UNITY_SPACE_NAVIGATION_STATE_EVENT,
        handleCanonicalState,
      );
      signal?.removeEventListener("abort", handleAbort);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };

    const finish = (target: UploadSpaceTargetForImport) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(target);
    };

    function handleCanonicalState() {
      const nextTarget = getUploadSpaceTargetForImport();
      if (isResolvedUploadSpaceTarget(nextTarget)) {
        finish(nextTarget);
      }
    }

    function handleAbort() {
      finish(getUploadSpaceTargetForImport());
    }

    window.addEventListener(
      UNITY_SPACE_NAVIGATION_STATE_EVENT,
      handleCanonicalState,
    );
    signal?.addEventListener("abort", handleAbort);
    timeoutId = window.setTimeout(() => {
      finish(getUploadSpaceTargetForImport());
    }, timeoutMs);
  });
}

/**
 * Space 삭제와 그 Undo/Redo는 transaction 없이 Unity 현재 Space를 바꿔 canonical state를 보내지 않는다.
 * 그때 오는 `unityToReact_spaceChanged`의 index·개수로 마지막 canonical snapshot을 맞춘다.
 * 전환 transaction 중에는 그 transaction의 canonical state가 정본이므로 건드리지 않는다.
 */
export function adoptUnitySpaceChangedOutsideTransaction(
  spaceIndex: unknown,
  spacesCount: unknown,
): boolean {
  const index = normalizeSpaceIndex(spaceIndex);
  const count = normalizeCount(spacesCount);
  if (
    !latestAcceptedPayload ||
    index === null ||
    count === null ||
    index >= count ||
    isSpaceSwitchInProgressForUpload()
  ) {
    return false;
  }
  latestAcceptedPayload = {
    ...latestAcceptedPayload,
    currentSpaceIndex: index,
    targetSpaceIndex: index,
    spacesCount: count,
  };
  return true;
}

export function resetCanonicalSpaceSnapshotForTests(): void {
  latestAcceptedPayload = null;
  trackerState.transactionId = null;
  trackerState.highestPhaseRank = -1;
  trackerState.viewrDataRevision = null;
  trackerState.projectIdentity = null;
  trackerState.canonicalSeen = false;
  trackerState.operation = null;
  timingState.transactionId = null;
  timingState.startedAtMs = null;
  timingState.lastPhase = null;
  timingState.lastAtMs = null;
}

function resetTracker(transactionId: string, operation?: string | null): void {
  trackerState.transactionId = transactionId;
  trackerState.highestPhaseRank = -1;
  trackerState.viewrDataRevision = null;
  trackerState.projectIdentity = null;
  trackerState.canonicalSeen = false;
  trackerState.operation = operation ?? null;

  timingState.transactionId = transactionId;
  timingState.startedAtMs = null;
  timingState.lastPhase = null;
  timingState.lastAtMs = null;
}

function normalizeCount(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.trunc(value)
    : null;
}

function normalizeSpaceIndex(value: unknown): number | null {
  const normalized = normalizeCount(value);
  return normalized !== null && normalized >= 0 ? normalized : null;
}

function normalizeBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function normalizeString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function parseJsonCandidate(value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function unwrapNavigationStateCandidate(payload: unknown): unknown {
  let candidate = parseJsonCandidate(payload);
  if (typeof candidate !== "object" || candidate === null) {
    return candidate;
  }

  const detail = candidate as Record<string, unknown>;
  if (typeof detail.transactionId === "string" && typeof detail.phase === "string") {
    return detail;
  }

  const nestedCandidates = [detail.payload, detail.data, detail.message];
  for (const nested of nestedCandidates) {
    const parsedNested = parseJsonCandidate(nested);
    if (typeof parsedNested !== "object" || parsedNested === null) {
      continue;
    }
    const nestedDetail = parsedNested as Record<string, unknown>;
    if (
      typeof nestedDetail.transactionId === "string" &&
      typeof nestedDetail.phase === "string"
    ) {
      return nestedDetail;
    }
  }

  return detail;
}

function normalizePhase(
  phase: unknown,
): UnitySpaceNavigationStatePayload["phase"] | null {
  switch (phase) {
    case "started":
    case "rehydrate_received":
    case "viewr_data_ready":
    case "target_space_applied":
    case "runtime_restored":
    case "restore_ready":
    case "awaiting_space_confirmation":
    case "space_confirmed":
    case "vse_model_ready":
    case "stabilized":
    case "confirmed":
    case "completed":
    case "failed":
      return phase;
    case "space_ready":
      return "space_confirmed";
    default:
      return null;
  }
}

export function normalizeUnitySpaceNavigationStatePayload(
  payload: unknown,
): UnitySpaceNavigationStatePayload | null {
  const candidate = unwrapNavigationStateCandidate(payload);

  if (typeof candidate !== "object" || candidate === null) {
    return null;
  }

  const detail = candidate as Record<string, unknown>;
  if (typeof detail.transactionId !== "string" || typeof detail.phase !== "string") {
    return null;
  }

  const operation =
    detail.operation === "runtime_rehydrate" ||
    detail.operation === "project_reload" ||
    detail.operation === "select_space" ||
    detail.operation === "remount_only"
      ? detail.operation
      : "project_reload";
  const phase = normalizePhase(detail.phase);

  if (!phase) {
    return null;
  }

  return {
    transactionId: detail.transactionId,
    runId: normalizeCount(detail.runId),
    operation,
    phase,
    status:
      detail.status === "success" ||
      detail.status === "failed" ||
      detail.status === "in_progress"
        ? detail.status
        : phase === "completed"
          ? "success"
          : phase === "failed"
            ? "failed"
            : "in_progress",
    targetSpaceIndex: normalizeCount(detail.targetSpaceIndex),
    currentSpaceIndex: normalizeCount(detail.currentSpaceIndex),
    spacesCount: normalizeCount(detail.spacesCount),
    hasRuntimeVseInCurrentSpace: normalizeBoolean(detail.hasRuntimeVseInCurrentSpace),
    runtimeVseCountInCurrentSpace: normalizeCount(detail.runtimeVseCountInCurrentSpace),
    hasLoadableVseInCurrentSpace: normalizeBoolean(detail.hasLoadableVseInCurrentSpace),
    loadableVseCountInCurrentSpace: normalizeCount(detail.loadableVseCountInCurrentSpace),
    vsePhase: normalizeString(detail.vsePhase),
    vseStatus: normalizeString(detail.vseStatus),
    activeVseLoadCount: normalizeCount(detail.activeVseLoadCount),
    completedVseLoadCount: normalizeCount(detail.completedVseLoadCount),
    failedVseLoadCount: normalizeCount(detail.failedVseLoadCount),
    noLoadableVseInCurrentSpace: normalizeBoolean(detail.noLoadableVseInCurrentSpace),
    hmiRestoreStatus:
      detail.hmiRestoreStatus === "not_required" ||
      detail.hmiRestoreStatus === "in_progress" ||
      detail.hmiRestoreStatus === "ready" ||
      detail.hmiRestoreStatus === "failed"
        ? detail.hmiRestoreStatus
        : null,
    restoredHmiPlaneCount: normalizeCount(detail.restoredHmiPlaneCount),
    skippedHmiPlaneCount: normalizeCount(detail.skippedHmiPlaneCount),
    thumbnailCacheHits: normalizeCount(detail.thumbnailCacheHits),
    uploadButtonEnabled: normalizeBoolean(detail.uploadButtonEnabled),
    viewrDataRevision: normalizeCount(detail.viewrDataRevision),
    projectIdentity:
      typeof detail.projectIdentity === "string" ? detail.projectIdentity : null,
    expectsExplicitSpaceSelect: normalizeBoolean(detail.expectsExplicitSpaceSelect),
    selectionCommandAccepted: normalizeBoolean(detail.selectionCommandAccepted),
    selectionCommandRejectedReason:
      typeof detail.selectionCommandRejectedReason === "string"
        ? detail.selectionCommandRejectedReason
        : null,
    reasonCode: typeof detail.reasonCode === "string" ? detail.reasonCode : null,
    message: typeof detail.message === "string" ? detail.message : null,
  };
}

export function hasCanonicalSpaceNavigationStateForTransaction(
  transactionId?: string | null,
): boolean {
  if (!transactionId) {
    return false;
  }
  return (
    trackerState.canonicalSeen &&
    trackerState.transactionId === transactionId
  );
}

export function hasCanonicalSpaceNavigationStartedForTransaction(
  transactionId?: string | null,
): boolean {
  return hasCanonicalSpaceNavigationStateForTransaction(transactionId);
}

export function hasCanonicalSpaceNavigationReachedPhase(
  transactionId: string | null | undefined,
  phase: UnitySpaceNavigationStatePayload["phase"],
): boolean {
  if (
    !transactionId ||
    !trackerState.canonicalSeen ||
    trackerState.transactionId !== transactionId
  ) {
    return false;
  }

  return trackerState.highestPhaseRank >= getPhaseRank(phase);
}

function shouldIgnorePayload(
  payload: UnitySpaceNavigationStatePayload,
): boolean {
  if (
    trackerState.projectIdentity &&
    payload.projectIdentity &&
    trackerState.projectIdentity !== payload.projectIdentity
  ) {
    return true;
  }

  if (
    trackerState.viewrDataRevision !== null &&
    payload.viewrDataRevision !== null &&
    payload.viewrDataRevision < trackerState.viewrDataRevision
  ) {
    return true;
  }

  const nextRank = PHASE_RANK[payload.phase];
  if (
    trackerState.highestPhaseRank >= PHASE_RANK.completed &&
    payload.phase !== "failed"
  ) {
    if (
      payload.phase === "completed" &&
      latestAcceptedPayload?.phase !== "completed"
    ) {
      return false;
    }
    return true;
  }
  if (payload.phase !== "failed" && nextRank < trackerState.highestPhaseRank) {
    return true;
  }

  return false;
}

function isEditorImportProjectReload(
  payload: UnitySpaceNavigationStatePayload,
): boolean {
  return (
    payload.operation === "project_reload" &&
    isEditorImportReloadCorrelationTransaction(payload.transactionId) &&
    // Home restore registers a real navigation owner before loading metadata.
    // It must use the normal transaction/run freshness checks.
    !payload.transactionId.startsWith("editor-import-reload:restore:")
  );
}

function shouldAcceptTransactionForTracker(
  payload: UnitySpaceNavigationStatePayload,
): boolean {
  if (trackerState.transactionId === payload.transactionId) {
    return true;
  }

  if (isEditorImportProjectReload(payload)) {
    return true;
  }

  const activeTransactionId =
    typeof window !== "undefined"
      ? window.__viewr_spaceSwitchTransactionId ?? null
      : null;

  if (activeTransactionId) {
    return payload.transactionId === activeTransactionId;
  }

  return trackerState.transactionId === null;
}

function updateTracker(payload: UnitySpaceNavigationStatePayload): void {
  trackerState.canonicalSeen = true;
  trackerState.highestPhaseRank = Math.max(
    trackerState.highestPhaseRank,
    getPhaseRank(payload.phase),
  );
  if (payload.viewrDataRevision !== null) {
    trackerState.viewrDataRevision = payload.viewrDataRevision;
  }
  if (payload.projectIdentity) {
    trackerState.projectIdentity = payload.projectIdentity;
  }
  if (payload.operation) {
    trackerState.operation = payload.operation;
  }
}

function recordCanonicalPhaseTiming(payload: UnitySpaceNavigationStatePayload): void {
  const now = Date.now();
  if (timingState.transactionId !== payload.transactionId) {
    timingState.transactionId = payload.transactionId;
    timingState.startedAtMs = null;
    timingState.lastPhase = null;
    timingState.lastAtMs = null;
  }

  if (timingState.startedAtMs === null) {
    timingState.startedAtMs = now;
  }

  const elapsedFromStartMs =
    timingState.startedAtMs !== null ? now - timingState.startedAtMs : null;
  const elapsedFromPreviousMs =
    timingState.lastAtMs !== null ? now - timingState.lastAtMs : null;
  const previousPhase = timingState.lastPhase;

  timingState.lastPhase = payload.phase;
  timingState.lastAtMs = now;

  // Keep this always-on but low-noise: one line per canonical phase accepted.
  logEditorCorrelation(
    "space_navigation_phase_timing",
    {
      phase: payload.phase,
      source: "spaceNavigationCanonicalState",
    },
    {
      transactionId: payload.transactionId,
      runId: payload.runId ?? null,
      operation: payload.operation,
      phase: payload.phase,
      previousPhase,
      elapsedFromStartMs,
      elapsedFromPreviousMs,
      currentSpaceIndex: payload.currentSpaceIndex ?? null,
      targetSpaceIndex: payload.targetSpaceIndex ?? null,
      stage: getActiveSpaceSwitchStage(),
      owner: "unity",
    },
  );
}

function dispatchCanonicalWindowEvent(
  payload: UnitySpaceNavigationStatePayload,
): void {
  try {
    window.dispatchEvent(
      new CustomEvent(UNITY_SPACE_NAVIGATION_STATE_EVENT, {
        detail: payload,
      }),
    );
  } catch {
    // ignore
  }
}

function requiresUnityConfirmedCurrentSpace(
  phase: UnitySpaceNavigationStatePayload["phase"],
): boolean {
  return (
    phase === "target_space_applied" ||
    phase === "space_confirmed" ||
    phase === "vse_model_ready" ||
    phase === "stabilized" ||
    phase === "confirmed" ||
    phase === "completed"
  );
}

function warnMissingCurrentSpaceIndex(
  payload: UnitySpaceNavigationStatePayload,
): void {
  logEditorCorrelation(
    "space_navigation_canonical_missing_current_space_index",
    {
      phase: payload.phase,
      source: "spaceNavigationCanonicalState",
    },
    {
      transactionId: payload.transactionId,
      runId: payload.runId ?? null,
      targetSpaceIndex: payload.targetSpaceIndex ?? null,
      status: payload.status,
      owner: "unity",
      reason: "currentSpaceIndex_required_for_completion_phase",
      stage: getActiveSpaceSwitchStage(),
    },
    "warn",
  );
}

function reconcilePendingAddSpaceCanonicalTarget(
  payload: UnitySpaceNavigationStatePayload,
): boolean {
  if (typeof window === "undefined") return false;
  const pendingTransactionId = window.__viewr_pendingAddSpaceTransactionId ?? null;
  const pendingSpaceIndex =
    typeof window.__viewr_pendingAddSpaceIndex === "number"
      ? window.__viewr_pendingAddSpaceIndex
      : null;
  if (
    !pendingTransactionId ||
    pendingSpaceIndex === null ||
    payload.transactionId !== pendingTransactionId
  ) {
    return false;
  }
  const payloadMatchesPendingIndex =
    payload.targetSpaceIndex === pendingSpaceIndex ||
    payload.currentSpaceIndex === pendingSpaceIndex;
  if (!payloadMatchesPendingIndex) {
    return false;
  }

  const previousActiveTargetSpaceIndex =
    typeof window.__viewr_spaceSwitchTargetIndex === "number"
      ? window.__viewr_spaceSwitchTargetIndex
      : null;
  setSpaceSwitchTargetIndex(pendingSpaceIndex);
  logEditorCorrelation(
    "space_add_target_reconciled_from_unity_canonical",
    {
      phase: "freshness",
      source: `spaceNavigationCanonicalState:${payload.phase}`,
    },
    {
      transactionId: payload.transactionId,
      previousActiveTargetSpaceIndex,
      reconciledTargetSpaceIndex: pendingSpaceIndex,
      payloadTargetSpaceIndex: payload.targetSpaceIndex,
      payloadCurrentSpaceIndex: payload.currentSpaceIndex,
      operation: payload.operation,
    },
    "warn",
  );
  return true;
}

export function applyUnitySpaceNavigationState(
  payload: UnitySpaceNavigationStatePayload,
): boolean {
  if (__DEV_CANONICAL_DEBUG__) {
    console.log("[CanonicalState] received:", {
      phase: payload.phase,
      operation: payload.operation,
      transactionId: payload.transactionId,
      windowTxId: window.__viewr_spaceSwitchTransactionId ?? null,
      trackerTxId: trackerState.transactionId,
      trackerHighestRank: trackerState.highestPhaseRank,
    });
  }

  const freshnessRejected =
    typeof window !== "undefined" &&
    window.__viewr_spaceSwitchTransactionId &&
    !isEditorImportProjectReload(payload) &&
    !isLatestSpaceSwitchEvent(
      {
        transactionId: payload.transactionId,
        runId: payload.runId ?? null,
        targetSpaceIndex: payload.targetSpaceIndex ?? null,
      },
      {
        source: `spaceNavigationCanonicalState:${payload.phase}`,
        allowLegacyNoTransaction: true,
        allowMissingRunId: true,
        allowMissingTargetSpaceIndex: true,
      },
    );
  if (freshnessRejected && !reconcilePendingAddSpaceCanonicalTarget(payload)) {
    if (__DEV_CANONICAL_DEBUG__) {
      console.warn("[CanonicalState] DROPPED by isLatestSpaceSwitchEvent:", {
        phase: payload.phase,
        payloadTxId: payload.transactionId,
        windowTxId: window.__viewr_spaceSwitchTransactionId,
      });
    }
    return false;
  }

  if (!shouldAcceptTransactionForTracker(payload)) {
    if (__DEV_CANONICAL_DEBUG__) {
      console.warn("[CanonicalState] DROPPED by shouldAcceptTransactionForTracker:", {
        phase: payload.phase,
        payloadTxId: payload.transactionId,
        trackerTxId: trackerState.transactionId,
        activeWindowTxId: window.__viewr_spaceSwitchTransactionId ?? null,
      });
    }
    return false;
  }

  if (trackerState.transactionId !== payload.transactionId) {
    resetTracker(payload.transactionId, payload.operation);
  } else if (
    payload.operation &&
    trackerState.operation &&
    payload.operation !== trackerState.operation &&
    trackerState.highestPhaseRank >= PHASE_RANK.failed
  ) {
    if (__DEV_CANONICAL_DEBUG__) {
      console.log("[CanonicalState] RESET tracker: operation changed after failure", {
        txId: payload.transactionId,
        prevOperation: trackerState.operation,
        nextOperation: payload.operation,
        prevHighestRank: trackerState.highestPhaseRank,
      });
    }
    resetTracker(payload.transactionId, payload.operation);
  }

  if (payload.transactionId.startsWith("editor-import-reload:restore:") &&
      payload.status === "success" &&
      (payload.phase === "confirmed" || payload.phase === "completed") &&
      payload.activeVseLoadCount !== 0) {
    // FileLoadHandler can emit completed before its finally decrements the load
    // count. Keep the phase open for the later lifecycle terminal with count 0.
    return false;
  }

  const payloadCurrentSpaceIndex = normalizeSpaceIndex(payload.currentSpaceIndex);
  const payloadTargetSpaceIndex = normalizeSpaceIndex(payload.targetSpaceIndex);
  const awaitsTargetAfterProjectReload =
    payload.operation === "project_reload" &&
    requiresUnityConfirmedCurrentSpace(payload.phase) &&
    payloadCurrentSpaceIndex !== null &&
    payloadTargetSpaceIndex !== null &&
    payloadCurrentSpaceIndex !== payloadTargetSpaceIndex;
  const acceptedPayload: UnitySpaceNavigationStatePayload = {
    ...payload,
    phase: awaitsTargetAfterProjectReload ? "restore_ready" : payload.phase,
    status: awaitsTargetAfterProjectReload ? "in_progress" : payload.status,
    currentSpaceIndex: payloadCurrentSpaceIndex,
    targetSpaceIndex: payloadTargetSpaceIndex,
  };

  if (shouldIgnorePayload(acceptedPayload)) {
    if (__DEV_CANONICAL_DEBUG__) {
      console.warn("[CanonicalState] DROPPED by shouldIgnorePayload:", {
        phase: acceptedPayload.phase,
        payloadTxId: acceptedPayload.transactionId,
        trackerHighestRank: trackerState.highestPhaseRank,
        payloadRank: PHASE_RANK[acceptedPayload.phase],
        projectIdentityMatch:
          trackerState.projectIdentity === acceptedPayload.projectIdentity,
      });
    }
    return false;
  }

  const effectiveConfirmedSpaceIndex =
    payloadCurrentSpaceIndex ?? payloadTargetSpaceIndex;

  updateTracker(acceptedPayload);
  latestAcceptedPayload = acceptedPayload;
  recordCanonicalPhaseTiming(acceptedPayload);
  markSpaceNavigationCanonicalStateSeen({
    transactionId: acceptedPayload.transactionId,
    source: `spaceNavigationCanonicalState:${acceptedPayload.phase}`,
  });

  if (payloadCurrentSpaceIndex !== null) {
    window.__viewr_currentSpaceIndex = payloadCurrentSpaceIndex;
  }

  dispatchCanonicalWindowEvent(acceptedPayload);

  if (__DEV_CANONICAL_DEBUG__) {
    console.log("[CanonicalState] DISPATCHED:", {
      phase: acceptedPayload.phase,
      operation: acceptedPayload.operation,
      transactionId: acceptedPayload.transactionId,
      currentSpaceIndex: acceptedPayload.currentSpaceIndex,
      targetSpaceIndex: acceptedPayload.targetSpaceIndex,
    });
  }

  if (
    requiresUnityConfirmedCurrentSpace(acceptedPayload.phase) &&
    effectiveConfirmedSpaceIndex === null
  ) {
    warnMissingCurrentSpaceIndex(acceptedPayload);
    return true;
  }

  const restoreMode =
    acceptedPayload.operation === "runtime_rehydrate" ? "rehydrate" : "reload";

  switch (acceptedPayload.phase) {
    case "viewr_data_ready":
    case "runtime_restored":
    case "restore_ready":
      markSpaceNavigationRuntimeRestored({
        transactionId: acceptedPayload.transactionId,
        targetSpaceIndex: payloadTargetSpaceIndex,
        currentSpaceIndex: payloadCurrentSpaceIndex,
        restoreMode,
        hasLoadableVseInCurrentSpace:
          acceptedPayload.hasLoadableVseInCurrentSpace ?? undefined,
        loadableVseCountInCurrentSpace:
          acceptedPayload.loadableVseCountInCurrentSpace ?? undefined,
        source: `spaceNavigationCanonicalState:${acceptedPayload.phase}`,
      });
      return true;
    case "awaiting_space_confirmation":
      markSpaceNavigationSelecting({
        transactionId: acceptedPayload.transactionId,
        targetSpaceIndex: payloadTargetSpaceIndex,
        source: "spaceNavigationCanonicalState:awaiting_space_confirmation",
      });
      return true;
    case "target_space_applied":
    case "space_confirmed":
      confirmSpaceNavigation({
        transactionId: acceptedPayload.transactionId,
        targetSpaceIndex: payloadTargetSpaceIndex,
        confirmedSpaceIndex: effectiveConfirmedSpaceIndex,
        currentSpaceIndex: effectiveConfirmedSpaceIndex,
        status: acceptedPayload.status,
        hasLoadableVseInCurrentSpace:
          acceptedPayload.hasLoadableVseInCurrentSpace ?? undefined,
        loadableVseCountInCurrentSpace:
          acceptedPayload.loadableVseCountInCurrentSpace ?? undefined,
        source: "spaceNavigationCanonicalState:space_confirmed",
      });
      return true;
    case "vse_model_ready":
    case "stabilized":
      markSpaceNavigationVseModelReady({
        transactionId: acceptedPayload.transactionId,
        source: `spaceNavigationCanonicalState:${acceptedPayload.phase}`,
      });
      return true;
    case "confirmed":
    case "completed":
      completeSpaceNavigationFromUnity({
        transactionId: acceptedPayload.transactionId,
        runId: acceptedPayload.runId ?? undefined,
        targetSpaceIndex: payloadTargetSpaceIndex,
        currentSpaceIndex: effectiveConfirmedSpaceIndex,
        hasLoadableVseInCurrentSpace:
          acceptedPayload.hasLoadableVseInCurrentSpace ?? undefined,
        loadableVseCountInCurrentSpace:
          acceptedPayload.loadableVseCountInCurrentSpace ?? undefined,
        status: acceptedPayload.status,
        source: "spaceNavigationCanonicalState:completed",
      });
      return true;
    case "failed":
      logEditorCorrelation(
        "space_switch_background_failed",
        {
          phase: "unity_failed",
          source: "spaceNavigationCanonicalState",
        },
        {
          transactionId: acceptedPayload.transactionId,
          runId: acceptedPayload.runId ?? null,
          targetSpaceIndex: payloadTargetSpaceIndex,
          currentSpaceIndex: payloadCurrentSpaceIndex,
          reason: acceptedPayload.reasonCode ?? "space_navigation_failed",
          status: acceptedPayload.status,
          stage: getActiveSpaceSwitchStage(),
          owner: "unity",
        },
        "warn",
      );
      return true;
    default:
      return true;
  }
}
